import { SpinnerIcon } from '@acceso/design-system';
import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import clsx from 'clsx';
import React, { Suspense, useEffect } from 'react';
import { Group, Panel } from 'react-resizable-panels';
import { useLocation } from 'react-router-dom';

import { BroadcastScheduleProgramBodySegmentToolkit } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/BroadcastScheduleProgramBodySegmentToolkit';
import { useBroadcastScheduleClipsFilters } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitCuts/components/store/broadcast-schedule-clips-filters';
import { ESegmentToolkit } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { useCutsListSource } from '@/features/broadcast-schedule-program/hooks/use-cuts-list-source';
import { useNavigationList } from '@/features/broadcast-schedule-program/hooks/useNavigationList';
import { useGetClipById } from '@/features/clips/api/get-clip-by-id';
import { useClipPermissions } from '@/features/clips/hooks/useClipPermissions';
import { useReport } from '@/stores/report-store';

import { usePatchSegment } from '../../api/patch-segment';
import { TSegment } from '../../types';
import { SegmentsDetailCard } from '../SegmentsDetailCard/SegmentsDetailCard';

import { SegmentCard } from './SegmentCard';

type TSegmentBodyProps = {
  segmentsData: UseInfiniteQueryResult<
    InfiniteData<
      {
        data: TSegment[];
        timestamp: string;
        offset: number;
        total: number;
      },
      unknown
    >,
    Error
  >;
};

export function SegmentBody({ segmentsData }: TSegmentBodyProps) {
  const {
    visibleBroadcastProgramCards,
    pinnedId,
    toolkitComponents,
    toolkitCardRegionRef,
    segmentItems,
    activeSegment,
    isHeaderVisible,
    setSegmentItems,
    setActiveSegment,
    setVisibleBroadcastProgramCards,
    setDrawerLogo,
    setVisibleBox,
    handleTooltikClick,
    setFormBox,
    setCutData,
  } = useBroadcastScheduleProgram();
  const { filters } = useBroadcastScheduleClipsFilters();
  const { mutateAsync: patchSegment } = usePatchSegment();
  const { setReport, setReportedId, setReportType } = useReport();
  useCutsListSource({
    kind: 'segment',
    id: activeSegment?.id,
    filters: { workspaces: filters.workspaces as string[] },
  });

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const clipId = searchParams.get('clip_id');
  const { data: clipIdData = null } = useGetClipById({ id: clipId || '' });

  useEffect(() => {
    if (!clipId) return;

    if (clipIdData) {
      setFormBox('cut');
      setCutData(clipIdData);
      handleTooltikClick(ESegmentToolkit.CORTES);
    }
  }, [clipId, clipIdData, setVisibleBroadcastProgramCards]);

  const { handleGoToNext, handleGoToPrevious, handleGoToFirst, handleGoToLast } = useNavigationList(
    {
      items: segmentItems,
      activeItem: activeSegment,
      setActiveItem: setActiveSegment,
    }
  );

  const { canSee: canSeeClips } = useClipPermissions();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setDrawerLogo(false);
        setVisibleBox(null);
      }
      if (event.altKey && event.key === 'ArrowDown') {
        handleGoToNext();
      }
      if (event.altKey && event.key === 'ArrowUp') {
        handleGoToPrevious();
      }
      if (event.ctrlKey && event.key.toLocaleLowerCase() === 'home') {
        handleGoToFirst();
      }
      if (event.ctrlKey && event.key === 'End') {
        handleGoToLast();
      }
      if (event.ctrlKey && event.altKey && event.key.toLowerCase() === 'c') {
        handleTooltikClick(ESegmentToolkit.CORTES);
      }
      if (event.ctrlKey && event.key === 'F12') {
        if (!activeSegment) return;

        setReportType('segment');
        setReport(true);
        setReportedId(activeSegment.id);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    setDrawerLogo,
    setVisibleBox,
    patchSegment,
    setActiveSegment,
    setSegmentItems,
    handleGoToNext,
    handleGoToPrevious,
    handleGoToFirst,
    handleGoToLast,
    activeSegment,
  ]);

  return (
    <Group
      className={clsx(
        'flex flex-col gap-2 lg:flex-row',
        isHeaderVisible ? 'lg:max-h-[calc(100vh-150px)]' : 'lg:max-h-[calc(100vh-75px)]'
      )}
    >
      {visibleBroadcastProgramCards?.segments && (
        <Panel minSize={300} maxSize="25%">
          <SegmentCard segmentsData={segmentsData} segments />
        </Panel>
      )}
      {visibleBroadcastProgramCards?.segmentDetail && (
        <Panel minSize="35%">
          <SegmentsDetailCard />
        </Panel>
      )}
      {canSeeClips && pinnedId && toolkitComponents[pinnedId] && (
        <Suspense fallback={<SpinnerIcon className="animation infinite" />}>
          <Panel minSize={300}>
            <div
              ref={toolkitCardRegionRef}
              className="order-3 w-full flex-auto overflow-auto lg:order-2"
            >
              {React.createElement(toolkitComponents[pinnedId], { segmentItems })}
            </div>
          </Panel>
        </Suspense>
      )}
      {visibleBroadcastProgramCards?.segmentToolkit && (
        <BroadcastScheduleProgramBodySegmentToolkit segments />
      )}
    </Group>
  );
}
